import { isNativePlatform } from './nativePlugins';

/**
 * Processes an image before upload by resizing and optionally cropping it
 * On native platforms (Android/iOS), uses smaller dimensions to avoid WebView memory issues
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @param maxHeight Maximum height of the processed image
 * @param crop Whether to crop the image to a square
 * @returns A Promise that resolves to a processed image Blob, or the original File as fallback
 */
export const processImageForUpload = async (
  file: File,
  maxWidth = 400,
  maxHeight = 400,
  crop = true
): Promise<Blob | File> => {
  const isNative = isNativePlatform();
  
  // On native platforms, use more conservative dimensions to avoid WebView memory issues
  const effectiveMaxWidth = isNative ? Math.min(maxWidth, 800) : maxWidth;
  const effectiveMaxHeight = isNative ? Math.min(maxHeight, 800) : maxHeight;
  
  if (isNative) {
    console.log(`[ImageProcessing] Native platform detected, using dimensions: ${effectiveMaxWidth}x${effectiveMaxHeight}`);
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Create canvas for resizing/cropping
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          URL.revokeObjectURL(img.src);
          console.warn('[ImageProcessing] Could not get canvas context, using original file');
          resolve(file); // Fallback to original file
          return;
        }
        
        // Calculate dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (crop) {
          // For square crop, take the smaller dimension
          const size = Math.min(width, height);
          
          // Calculate crop position (center crop)
          const offsetX = (width - size) / 2;
          const offsetY = (height - size) / 2;
          
          // Set canvas to square dimensions
          canvas.width = canvas.height = Math.min(size, effectiveMaxWidth);
          
          // Draw cropped and resized image
          ctx.drawImage(
            img, 
            offsetX, offsetY, size, size, // Source rectangle
            0, 0, canvas.width, canvas.height // Destination rectangle
          );
        } else {
          // Just resize while maintaining aspect ratio
          if (width > height) {
            if (width > effectiveMaxWidth) {
              height = height * (effectiveMaxWidth / width);
              width = effectiveMaxWidth;
            }
          } else {
            if (height > effectiveMaxHeight) {
              width = width * (effectiveMaxHeight / height);
              height = effectiveMaxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
        }
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src);
            if (blob) {
              console.log(`[ImageProcessing] Successfully processed image: ${file.name} -> ${blob.size} bytes`);
              resolve(blob);
            } else {
              console.warn('[ImageProcessing] Canvas to Blob conversion failed, using original file');
              resolve(file); // Fallback to original file
            }
          },
          'image/jpeg',
          0.85 // Quality (0.85 is a good balance between size and quality)
        );
      } catch (error) {
        URL.revokeObjectURL(img.src);
        console.warn('[ImageProcessing] Canvas processing failed, using original file:', error);
        resolve(file); // Fallback to original file instead of rejecting
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      console.warn('[ImageProcessing] Failed to load image, using original file');
      resolve(file); // Fallback to original file instead of rejecting
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validates an image file based on size and type
 * @param file The image file to validate
 * @param maxSizeMB Maximum size in MB
 * @returns An error message string if invalid, null if valid
 */
export const validateImage = (file: File, maxSizeMB = 5): string | null => {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  
  if (!validTypes.includes(file.type)) {
    return 'Please upload a valid image (JPEG, PNG, GIF, or WEBP)';
  }
  
  if (file.size > maxSizeMB * 1024 * 1024) {
    return `Image must be smaller than ${maxSizeMB}MB`;
  }
  
  return null;
};
